import {
    cloneElement,
    FC,
    isValidElement,
    JSX,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {ChevronDown20Regular, ChevronRight20Regular} from "@fluentui/react-icons";
import "./bento-tree-select.scss";

const parRestaurantLogo = new URL(
    "@/img/PAR-Restaurant-Logo-Positive.png",
    import.meta.url
).href;

interface FlatNode {
    id: string;
    pId: string;
    value: string;
    name: string;
    title: JSX.Element;
}

interface TreeNode extends FlatNode {
    children: TreeNode[];
}

interface BentoTreeSelectProps {
    groupTreeData: FlatNode[];
    selectedValue: string;
    conceptName: string;
    rootGroupId: string;
}

function buildTree(flat: FlatNode[]): TreeNode[] {
    const map = new Map<string, TreeNode>();
    for (const n of flat) {
        map.set(n.id, {...n, children: []});
    }
    const roots: TreeNode[] = [];
    for (const n of flat) {
        const node = map.get(n.id)!;
        if (!n.pId || !map.has(n.pId)) {
            roots.push(node);
        } else {
            map.get(n.pId)!.children.push(node);
        }
    }
    return roots;
}

function getAllParentIds(flat: FlatNode[]): Set<string> {
    const ids = new Set<string>();
    for (const n of flat) {
        if (n.pId) ids.add(n.pId);
    }
    return ids;
}

const BentoTreeSelect: FC<BentoTreeSelectProps> = ({
    groupTreeData,
    selectedValue,
    conceptName,
    rootGroupId
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [expandedIds, setExpandedIds] = useState<Set<string>>(() =>
        getAllParentIds(groupTreeData)
    );
    const containerRef = useRef<HTMLDivElement>(null);

    // When the hierarchy data changes, expand all parent nodes by default
    useEffect(() => {
        setExpandedIds(getAllParentIds(groupTreeData));
    }, [groupTreeData]);

    // Close on click outside or Escape
    useEffect(() => {
        if (!open) return;

        const onMouseDown = (e: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
                setSearch("");
            }
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setOpen(false);
                setSearch("");
            }
        };

        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    const tree = useMemo(() => buildTree(groupTreeData), [groupTreeData]);

    const filterLower = search.trim().toLowerCase();

    // When searching, compute the set of nodes to show (matches + their ancestor chain)
    const visibleIds = useMemo(() => {
        if (!filterLower) return null;
        const matched = new Set<string>();
        const nodeById = new Map(groupTreeData.map((n) => [n.id, n]));
        for (const n of groupTreeData) {
            if (n.name.toLowerCase().includes(filterLower)) {
                matched.add(n.id);
                let pid = n.pId;
                while (pid) {
                    matched.add(pid);
                    pid = nodeById.get(pid)?.pId ?? "";
                }
            }
        }
        return matched;
    }, [filterLower, groupTreeData]);

    const toggleExpand = (id: string) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleLinkClick = () => {
        setOpen(false);
        setSearch("");
    };

    const isRoot = (node: TreeNode) => !node.pId || node.id === rootGroupId;

    const renderNode = (node: TreeNode, depth: number): JSX.Element | null => {
        if (visibleIds && !visibleIds.has(node.id)) return null;

        const hasChildren = node.children.length > 0;
        const isExpanded = expandedIds.has(node.id) || !!filterLower;
        const isSelected = node.value === selectedValue;

        // Root node rendered as "All stores" — reuse the existing Link but replace its label
        const displayTitle =
            isRoot(node) && isValidElement(node.title)
                ? cloneElement(
                      node.title,
                      {},
                      <span className="bento-tree-select__all-stores">
                          All stores
                      </span>
                  )
                : node.title;

        return (
            <div key={node.id} className="bento-tree-select__node">
                <div
                    className={`bento-tree-select__row${
                        isSelected ? " bento-tree-select__row--selected" : ""
                    }`}
                    style={{paddingLeft: `${0.5 + depth * 1.25}rem`}}
                >
                    {hasChildren ? (
                        <button
                            type="button"
                            className="bento-tree-select__caret"
                            aria-label={isExpanded ? "Collapse" : "Expand"}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(node.id);
                            }}
                        >
                            {isExpanded ? (
                                <ChevronDown20Regular />
                            ) : (
                                <ChevronRight20Regular />
                            )}
                        </button>
                    ) : (
                        <span className="bento-tree-select__caret bento-tree-select__caret--leaf" />
                    )}
                    <span
                        className="bento-tree-select__label"
                        onClick={handleLinkClick}
                    >
                        {displayTitle}
                    </span>
                </div>
                {hasChildren && isExpanded && (
                    <div className="bento-tree-select__children">
                        {node.children.map((child) => renderNode(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    const selectedNode = groupTreeData.find((n) => n.value === selectedValue);
    const selectedName = selectedNode
        ? selectedNode.id === rootGroupId
            ? "All stores"
            : selectedNode.name
        : conceptName;

    return (
        <div className="bento-tree-select" ref={containerRef}>
            <button
                type="button"
                className={`bento-tree-select__trigger${
                    open ? " bento-tree-select__trigger--open" : ""
                }`}
                aria-haspopup="tree"
                aria-expanded={open}
                onClick={() => setOpen((prev) => !prev)}
            >
                <img
                    src={parRestaurantLogo}
                    alt="PAR Restaurant"
                    className="bento-tree-select__par-logo"
                />
                <span className="bento-tree-select__name">{selectedName}</span>
                <ChevronDown20Regular
                    className={`bento-tree-select__chevron${
                        open ? " bento-tree-select__chevron--up" : ""
                    }`}
                />
            </button>

            {open && (
                <div className="bento-tree-select__panel" role="tree">
                    <div className="bento-tree-select__search">
                        <input
                            autoFocus
                            type="search"
                            className="bento-tree-select__search-input"
                            placeholder="Search…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="bento-tree-select__tree">
                        {tree.map((node) => renderNode(node, 0))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BentoTreeSelect;
